import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { useDashboard } from '../api/dashboard'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import LeadDetailModal from '../components/leads/LeadDetailModal'
import { cn, formatDate, formatRelativeTime } from '../lib/utils'

function formatMoney(amount: string | number): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(0)}K`
  }
  return `${num.toFixed(0)}`
}

const leadStatusLabels: Record<string, string> = {
  new: 'Новая',
  bd_assigned: 'Назначена',
  call_scheduled: 'Звонок запланирован',
  call_done: 'Звонок проведён',
  qualified: 'Квалифицирована',
  estimation: 'Оценка',
  converted: 'Конвертирована',
  lost: 'Потеряна',
}

const leadStatusColors: Record<string, string> = {
  new: 'bg-accent-green/20 text-accent-green border-accent-green/30',
  bd_assigned: 'bg-accent-blue/20 text-accent-blue border-accent-blue/30',
  call_scheduled: 'bg-accent-orange/20 text-accent-orange border-accent-orange/30',
  call_done: 'bg-accent-violet/20 text-accent-violet border-accent-violet/30',
  qualified: 'bg-accent-emerald/20 text-accent-emerald border-accent-emerald/30',
  estimation: 'bg-accent-pink/20 text-accent-pink border-accent-pink/30',
  converted: 'bg-green-500/20 text-green-400 border-green-500/30',
  lost: 'bg-red-500/20 text-red-400 border-red-500/30',
}

const activityTypeIcons: Record<string, React.ReactNode> = {
  status_change: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="9 11 12 14 22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  ),
  note: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  document: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  ),
  call: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72" />
    </svg>
  ),
  email: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
}

export default function DashboardPage() {
  const user = useAuthStore((state) => state.user)
  const { data, isLoading } = useDashboard()
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null)

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Доброе утро'
    if (hour < 18) return 'Добрый день'
    return 'Добрый вечер'
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-text-secondary">Загрузка...</div>
      </div>
    )
  }

  const stats = data?.stats

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="animate-in">
        <h1 className="text-2xl font-bold text-white">
          {getGreeting()}, {user?.username || 'пользователь'}!
        </h1>
        <p className="text-text-secondary mt-1">
          Обзор вашей CRM системы
        </p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card hover className="animate-in delay-1">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-text-tertiary uppercase tracking-widest">
                  Новые заявки
                </p>
                <p className="text-3xl font-bold text-white mt-1">
                  {stats?.newLeads || 0}
                </p>
                <p className="text-xs text-text-tertiary mt-1">за 30 дней</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-accent-green/10 border border-accent-green/20 flex items-center justify-center">
                <svg className="w-6 h-6 text-accent-green" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card hover className="animate-in delay-2">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-text-tertiary uppercase tracking-widest">
                  Проекты в работе
                </p>
                <p className="text-3xl font-bold text-white mt-1">
                  {stats?.activeProjects || 0}
                </p>
                <p className="text-xs text-accent-green mt-1 font-mono">
                  {formatMoney(stats?.activeBudget || '0')} ₽
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-accent-blue/10 border border-accent-blue/20 flex items-center justify-center">
                <svg className="w-6 h-6 text-accent-blue" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M9 3v18" />
                  <path d="M15 3v18" />
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card hover className="animate-in delay-3">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-text-tertiary uppercase tracking-widest">
                  К оплате
                </p>
                <p className="text-3xl font-bold text-white mt-1">
                  {formatMoney(stats?.pendingPayments?.total || '0')} ₽
                </p>
                <p className="text-xs text-text-tertiary mt-1">
                  {stats?.pendingPayments?.count || 0} счетов
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-accent-orange/10 border border-accent-orange/20 flex items-center justify-center">
                <svg className="w-6 h-6 text-accent-orange" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card hover className="animate-in delay-4">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-text-tertiary uppercase tracking-widest">
                  Получено в этом месяце
                </p>
                <p className="text-3xl font-bold text-accent-green mt-1">
                  {formatMoney(stats?.receivedThisMonth || '0')} ₽
                </p>
                <p className="text-xs text-text-tertiary mt-1">
                  {stats?.totalClients || 0} клиентов
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-accent-emerald/10 border border-accent-emerald/20 flex items-center justify-center">
                <svg className="w-6 h-6 text-accent-emerald" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                  <polyline points="17 6 23 6 23 12" />
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Leads */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Последние заявки</CardTitle>
            <Link to="/leads" className="text-sm text-white hover:text-white/80 transition-colors">
              Все заявки →
            </Link>
          </CardHeader>
          <CardContent>
            {data?.recentLeads && data.recentLeads.length > 0 ? (
              <div className="space-y-2">
                {data.recentLeads.map((lead) => (
                  <div
                    key={lead.id}
                    onClick={() => setSelectedLeadId(lead.id)}
                    className="flex items-center gap-4 p-3 rounded-lg hover:bg-white/5 transition-colors cursor-pointer"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white truncate">{lead.name}</p>
                      <p className="text-sm text-text-tertiary truncate">
                        {lead.email || lead.phone || lead.telegram || 'Нет контакта'}
                      </p>
                    </div>
                    <span className={cn(
                      'text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap border',
                      leadStatusColors[lead.status]
                    )}>
                      {leadStatusLabels[lead.status]}
                    </span>
                    <span className="text-xs text-text-tertiary whitespace-nowrap">
                      {formatRelativeTime(lead.createdAt)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center py-8 text-text-tertiary">
                <p>Нет новых заявок</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Deadlines */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Ближайшие дедлайны</CardTitle>
            <Link to="/projects" className="text-sm text-white hover:text-white/80 transition-colors">
              Проекты →
            </Link>
          </CardHeader>
          <CardContent>
            {data?.upcomingDeadlines && data.upcomingDeadlines.length > 0 ? (
              <div className="space-y-2">
                {data.upcomingDeadlines.map((project) => (
                  <Link
                    key={project.id}
                    to={`/projects/${project.id}`}
                    className="block p-3 rounded-lg hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {project.stage && (
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: project.stage.color }}
                        />
                      )}
                      <p className="font-medium text-white truncate">{project.name}</p>
                    </div>
                    <p className="text-sm text-accent-orange">
                      {project.deadline && formatDate(project.deadline)}
                    </p>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center py-8 text-text-tertiary">
                <p>Нет ближайших дедлайнов</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Activity & Projects by Stage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Projects by Stage */}
        <Card>
          <CardHeader>
            <CardTitle>Проекты по стадиям</CardTitle>
          </CardHeader>
          <CardContent>
            {data?.projectsByStage && data.projectsByStage.length > 0 ? (
              <div className="space-y-3">
                {data.projectsByStage.map((stage, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <span
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: stage.color }}
                    />
                    <span className="flex-1 text-sm text-white">{stage.name}</span>
                    <span className="text-sm font-mono font-semibold text-white">{stage.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center py-8 text-text-tertiary">
                <p>Нет проектов</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle>Последняя активность</CardTitle>
          </CardHeader>
          <CardContent>
            {data?.recentActivities && data.recentActivities.length > 0 ? (
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {data.recentActivities.map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-text-tertiary flex-shrink-0">
                      {activityTypeIcons[activity.type] || activityTypeIcons.note}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{activity.content}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {activity.user && (
                          <span className="text-xs text-text-tertiary">
                            {activity.user.username}
                          </span>
                        )}
                        <span className="text-xs text-text-tertiary">
                          {formatRelativeTime(activity.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center py-8 text-text-tertiary">
                <p>Нет активности</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Lead Detail Modal */}
      {selectedLeadId && (
        <LeadDetailModal
          leadId={selectedLeadId}
          isOpen={!!selectedLeadId}
          onClose={() => setSelectedLeadId(null)}
        />
      )}
    </div>
  )
}
